import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import EmptyState from "../../common/DataList/EmptyState";
import ModaleLocale from "../../common/CustomModal/ModaleLocale";
import ModaleSite from "./ModaleSite";
import {
  listerSitesDeOperateur,
  listerThemes,
  listerVillesReunion,
  supprimerSite,
  voirSite,
} from "./operateursApi";

// ============================================================
// Onglet « Sites et calendriers » : les sites de la structure
// (GET /operateurs/:id/sites), avec leurs activités, leurs créneaux libres sur
// quatre semaines et leurs alertes (SANS_BOX, SANS_CRENEAU). Le calendrier
// d'un site s'ouvre avec l'écran existant /sites/:id/calendar. Création,
// modification et suppression passent par les routes /sites. En
// `lectureSeule` (vue de l'opérateur connecté, CE-1407), seuls le calendrier
// et ses créneaux restent accessibles.
// ============================================================

const OngletSites = ({ operateur, onFicheARafraichir, lectureSeule = false }) => {
  const [sites, setSites] = useState(null);
  const [villes, setVilles] = useState([]);
  const [themes, setThemes] = useState([]);
  const [modale, setModale] = useState({ ouverte: false, site: null });
  const [suppression, setSuppression] = useState(null); // { site, refus, enCours }

  const avecCalendrier = (operateur.regimes || []).length > 0;

  const chargerSites = () => {
    listerSitesDeOperateur(operateur.id).then(setSites, () => setSites([]));
  };

  useEffect(() => {
    chargerSites();
    listerVillesReunion().then(setVilles, () => {});
    listerThemes().then(setThemes, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operateur.id]);

  const ouvrirCreation = () => setModale({ ouverte: true, site: null });

  const ouvrirModification = (site) => {
    // Le read model de l'onglet ne porte pas les box, communes et fermetures :
    // la fiche complète du site vient de GET /sites/:id.
    voirSite(site.id).then(
      (complet) => setModale({ ouverte: true, site: complet }),
      (erreur) => toast.error(erreur.message)
    );
  };

  const surEnregistrement = (site, action) => {
    setModale({ ouverte: false, site: null });
    toast.success(
      action === "creation"
        ? `Le site ${site.name} a été créé.${avecCalendrier ? " Ouvrez son calendrier pour proposer des créneaux." : ""}`
        : `Le site ${site.name} a été modifié.`
    );
    chargerSites();
    onFicheARafraichir();
  };

  const confirmerSuppression = () => {
    setSuppression((s) => ({ ...s, enCours: true, refus: null }));
    supprimerSite(suppression.site.id).then(
      () => {
        toast.success(`Le site ${suppression.site.nom} a été supprimé.`);
        setSuppression(null);
        chargerSites();
        onFicheARafraichir();
      },
      (erreur) => setSuppression((s) => ({ ...s, enCours: false, refus: erreur.message }))
    );
  };

  if (sites === null) {
    return <p className="fiche-operateur__chargement">Chargement des sites…</p>;
  }

  const sitesEnAlerte = sites.filter((s) => (s.alertes || []).length > 0);

  return (
    <div className="onglet-sites">
      <div className="onglet-sites__tete">
        <p className="fiche-operateur__secondaire">
          {sites.length
            ? `${sites.length} site${sites.length > 1 ? "s" : ""} d'accueil.${
                avecCalendrier
                  ? " Chaque site a son calendrier, ses box de réception et les communes dont il reçoit les allocataires."
                  : " Sans régime, un site est une adresse du répertoire, sans box ni calendrier."
              }`
            : ""}
        </p>
        {!lectureSeule && (
          <div data-priv="crud_operateurs">
            <button type="button" className="btn btn-primary" onClick={ouvrirCreation}>
              <i className="fa fa-plus mr-2" aria-hidden="true" />
              Ajouter un site
            </button>
          </div>
        )}
      </div>

      {!sites.length ? (
        <section className="list-card">
          <EmptyState
            icon="fa-map-marker-alt"
            title="Aucun site d'accueil"
            hint={
              avecCalendrier
                ? "Un site porte l'adresse où l'opérateur reçoit, ses box et son calendrier. Il en faut au moins un pour la mise en service."
                : "Un site porte l'adresse et les activités proposées, citées par les contrats d'engagement."
            }
            action={lectureSeule ? null : { label: "Ajouter un site", onClick: ouvrirCreation }}
          />
        </section>
      ) : (
        <>
          {avecCalendrier && sitesEnAlerte.length > 0 && operateur.statut !== "INACTIF" && (
            <div className="encadre-alerte" role="status">
              <i className="fa fa-exclamation-triangle" aria-hidden="true" />
              <span>
                {sitesEnAlerte.map((s) => s.nom).join(", ")}
                {sitesEnAlerte.length > 1 ? " ont" : " a"} une alerte : sans box ou sans créneau
                ouvert sur les 4 prochaines semaines, les allocataires ne peuvent pas y choisir de
                date.
              </span>
            </div>
          )}
          <div className="cartes-sites">
            {sites.map((site) => {
              const sansBox = (site.alertes || []).includes("SANS_BOX");
              let calendrier = null;
              if (!site.aCalendrier) {
                calendrier = (
                  <div className="carte-site__calendrier carte-site__calendrier--muted">
                    <i className="fa fa-calendar-minus" aria-hidden="true" />
                    <span>Sans calendrier : adresse du répertoire.</span>
                  </div>
                );
              } else if (site.creneauxLibresQuatreSemaines > 0) {
                calendrier = (
                  <div className="carte-site__calendrier carte-site__calendrier--ok">
                    <i className="fa fa-calendar-check" aria-hidden="true" />
                    <span>
                      {site.creneauxLibresQuatreSemaines} créneau
                      {site.creneauxLibresQuatreSemaines > 1 ? "x" : ""} libre
                      {site.creneauxLibresQuatreSemaines > 1 ? "s" : ""} sur les 4 prochaines
                      semaines
                    </span>
                    <Link className="btn btn-ghost btn-sm" to={`/sites/${site.id}/calendar`}>
                      Ouvrir le calendrier
                    </Link>
                  </div>
                );
              } else {
                calendrier = (
                  <div className="carte-site__calendrier carte-site__calendrier--warn">
                    <i className="fa fa-calendar-times" aria-hidden="true" />
                    <span>{sansBox ? "Aucun box : le calendrier ne peut pas ouvrir de créneau." : "Aucun créneau ouvert."}</span>
                    <Link className="btn btn-ghost btn-sm" to={`/sites/${site.id}/calendar`}>
                      Ouvrir des créneaux
                    </Link>
                  </div>
                );
              }
              return (
                <article className="list-card carte-site" key={site.id}>
                  <header className="carte-site__tete">
                    <div>
                      <h3>{site.nom}</h3>
                      <p className="fiche-operateur__secondaire">
                        {[site.adresse, [site.codePostal, site.ville].filter(Boolean).join(" ")]
                          .filter(Boolean)
                          .join(", ") || "Adresse non renseignée"}
                      </p>
                    </div>
                    {!lectureSeule && (
                    <div className="carte-site__actions" data-priv="crud_operateurs">
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        onClick={() => ouvrirModification(site)}
                        aria-label={`Modifier le site ${site.nom}`}
                        title="Modifier le site"
                      >
                        <i className="fa fa-pen" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        onClick={() => setSuppression({ site, refus: null, enCours: false })}
                        aria-label={`Supprimer le site ${site.nom}`}
                        title="Supprimer le site"
                      >
                        <i className="fa fa-trash" aria-hidden="true" />
                      </button>
                    </div>
                    )}
                  </header>
                  <dl className="carte-site__faits">
                    {site.aCalendrier && (
                      <div>
                        <dt>Box de réception</dt>
                        <dd className={sansBox ? "carte-site__fait-alerte" : undefined}>
                          {site.nombreBoxes || "Aucun"}
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt>Contact</dt>
                      <dd>
                        {[site.telephone, site.email].filter(Boolean).join(" · ") || (
                          <span className="fact__empty">Non renseigné</span>
                        )}
                      </dd>
                    </div>
                    <div className="carte-site__fait-large">
                      <dt>Activités</dt>
                      {(site.sousThemes || []).length ? (
                        // Deux lignes au plus : la liste complète reste lisible au
                        // survol et par les lecteurs d'écran.
                        <dd className="carte-site__activites" title={site.sousThemes.map((st) => st.nom).join(", ")}>
                          {site.sousThemes.map((st) => st.nom).join(", ")}
                        </dd>
                      ) : (
                        <dd>
                          <span className="fact__empty">Aucune activité cochée</span>
                        </dd>
                      )}
                    </div>
                  </dl>
                  {calendrier}
                </article>
              );
            })}
          </div>
        </>
      )}

      <ModaleSite
        ouvert={modale.ouverte}
        onFermer={() => setModale({ ouverte: false, site: null })}
        operateur={operateur}
        site={modale.site}
        villes={villes}
        themes={themes}
        onEnregistre={surEnregistrement}
      />

      <ModaleLocale
        ouvert={!!suppression}
        onFermer={() => setSuppression(null)}
        titre={suppression ? `Supprimer le site ${suppression.site.nom} ?` : ""}
        taille="etroite"
        idTitre="modale-suppression-site"
        pied={
          suppression && (
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setSuppression(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmerSuppression}
                disabled={suppression.enCours}
              >
                Supprimer le site
              </button>
            </>
          )
        }
      >
        {suppression && (
          <>
            <p className="mb-0">
              Le site et ses créneaux libres seront supprimés. Un site cité par un contrat
              d'engagement, un avenant ou un compte rendu, ou sur lequel un rendez-vous est pris,
              ne peut pas être supprimé.
            </p>
            {suppression.refus && (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {suppression.refus}
              </div>
            )}
          </>
        )}
      </ModaleLocale>
    </div>
  );
};

export default OngletSites;
